import { getLoginUrl } from "@/const";
import { api } from "@convex/_generated/api";
import { useAuthActions } from "@convex-dev/auth/react";
import { useConvexAuth, useQuery } from "convex/react";
import { useCallback, useEffect, useMemo } from "react";

// F18 (2026-05-13): swap final do placeholder undefined que F3-2 deixou —
// causava "Cannot read properties of undefined (reading 'Symbol(functionName)')"
// no boot porque useQuery(undefined, ...) bate em Convex internals que esperam
// um function reference.
const currentUserQuery = api.users._currentUser;

type UseAuthOptions = {
  redirectOnUnauthenticated?: boolean;
  redirectPath?: string;
};

/**
 * useAuth — thin wrapper over @convex-dev/auth that returns the
 * shape the rest of the app already consumes:
 *   { user, loading, error, isAuthenticated, refresh, logout }
 *
 * Auth methods (for Login.tsx and similar):
 *   - signInPassword({ email, password, flow })   — "signIn" | "signUp"
 *   - signInOtp({ email })                        — Resend OTP provider
 *   - signInGoogle()                              — optional Google OAuth
 *   - logout()                                    — signs out
 */
export function useAuth(options?: UseAuthOptions) {
  const { redirectOnUnauthenticated = false, redirectPath = getLoginUrl() } =
    options ?? {};

  const { isLoading: authLoading, isAuthenticated } = useConvexAuth();
  const { signIn, signOut } = useAuthActions();

  // Skip the query until auth has resolved AND user is authenticated;
  // Convex returns `undefined` while loading, `null` if explicitly not found.
  const me = useQuery(
    currentUserQuery,
    !authLoading && isAuthenticated ? {} : "skip"
  );

  const loading = authLoading || (isAuthenticated && me === undefined);
  const user = isAuthenticated ? (me ?? null) : null;

  const logout = useCallback(async () => {
    await signOut();
  }, [signOut]);

  const signInPassword = useCallback(
    (email: string, password: string, flow: "signIn" | "signUp" = "signIn") =>
      signIn("password", { email, password, flow }),
    [signIn]
  );

  const signInOtp = useCallback(
    (email: string) => signIn("resend-otp", { email }),
    [signIn]
  );

  /**
   * Password reset — 2 etapas:
   *   1) `requestReset(email)` → dispara OTP via ResendOTPReset (subject "Redefinir...")
   *   2) `confirmReset({email, code, newPassword})` → valida OTP + troca hash + cria sessão
   *
   * Login.tsx chama (1) ao clicar "Esqueci a senha", (2) ao submeter o form
   * com novo código + senha. Convex Auth retorna o user autenticado, useConvexAuth
   * passa a isAuthenticated=true e o app redireciona pro dashboard.
   */
  const requestReset = useCallback(
    (email: string) => signIn("password", { email, flow: "reset" }),
    [signIn]
  );

  const confirmReset = useCallback(
    (email: string, code: string, newPassword: string) =>
      signIn("password", { email, code, newPassword, flow: "reset-verification" }),
    [signIn]
  );

  const signInGoogle = useCallback(() => signIn("google"), [signIn]);

  useEffect(() => {
    if (!redirectOnUnauthenticated) return;
    if (loading) return;
    if (isAuthenticated) return;
    if (typeof window === "undefined") return;
    if (window.location.pathname === redirectPath) return;
    window.location.href = redirectPath;
  }, [redirectOnUnauthenticated, redirectPath, loading, isAuthenticated]);

  return useMemo(
    () => ({
      user,
      loading,
      error: null as Error | null,
      isAuthenticated,
      refresh: () => {
        /* Convex queries are reactive — no manual refetch needed. */
      },
      logout,
      signInPassword,
      signInOtp,
      requestReset,
      confirmReset,
      signInGoogle,
    }),
    [
      user,
      loading,
      isAuthenticated,
      logout,
      signInPassword,
      signInOtp,
      requestReset,
      confirmReset,
      signInGoogle,
    ]
  );
}
